import type {
  Account,
  TonProofItemReplySuccess,
  Wallet,
} from '@tonconnect/sdk';
import type { UseFormSetError } from 'react-hook-form';
import useIsStepByStepEnabled from '~/client/components/Auth/Authenticate/AuthSteps/hooks/useIsStepByStepEnabled';
import type FormValues from '~/client/components/Auth/Register/RegisterBody/FormRegister/useFormRegister/FormValues';
import useRequestEmailVerification from '~/client/components/Auth/Register/RegisterBody/FormRegister/useFormRegister/useHandleRegister/useRequestEmailVerification';
import useUpdateGtmUserData from '~/client/components/Auth/Shared/utils/useUpdateGtmUserData';
import useCid from '~/client/hooks/cookies/marketing/useCid';
import useRefAff from '~/client/hooks/cookies/marketing/useRefAff';
import useRefCode from '~/client/hooks/cookies/marketing/useRefCode';
import useFriendRef from '~/client/hooks/cookies/useFriendRef';
import useSocialAuthType from '~/client/hooks/cookies/useSocialAuthType';
import useRouter from '~/client/hooks/router/useRouter';
import useLanguage from '~/client/hooks/useLanguage';
import useSite from '~/client/hooks/useSite';
import startFraudEngine from '~/client/shared/fraud/fraudEngine';
import useCheckWalletAuthErrors from '~/client/shared/WalletAuthentication/hooks/useCheckWalletAuthErrors';
import useAccountPopoverStore from '~/client/store/useAccountPopoverStore';
import trackEvent from '~/shared/trackEvent';
import Cookie from '~/shared/utils/Cookie';
import nativeReplace from '~/shared/utils/router/nativeReplace';
import {
  useTonConnectAuthMutation,
  type TonConnectAuthMutationVariables,
} from './graphql/TonConnectAuthMutation.generated';

type Args = {
  walletInfo: Wallet | null;
  values?: Omit<MetamaskDepositFormValues, 'dateOfBirth'> & {
    dateOfBirth?: number;
  };
  setError?: UseFormSetError<FormValues>;
  setErrors?: (errorsFragment: GenericErrorType[]) => void;
  setOtpError?: () => void;
  otp?: string;
  redirectUrl: string;
};

const useAuthTonConnectUser = () => {
  const [, setSocialAuthType] = useSocialAuthType();
  const isStepByStepEnabled = useIsStepByStepEnabled();
  const { asPath } = useRouter();
  const { code: language } = useLanguage();
  const [refAff] = useRefAff();
  const [friendRef] = useFriendRef();
  const refCode = useRefCode();
  const [cid] = useCid();
  const { site } = useSite();
  const [tonConnectAuth, { loading }] = useTonConnectAuthMutation();
  const { updateGtmUserData } = useUpdateGtmUserData();
  const { requestEmailVerification } = useRequestEmailVerification();
  const { dismissPopover } = useAccountPopoverStore();
  const { handleAuthErrors } = useCheckWalletAuthErrors('tonconnect');

  const handleTonConnectAuth = async ({
    walletInfo,
    values,
    setError,
    setErrors,
    setOtpError,
    otp,
    redirectUrl,
  }: Args) => {
    if (!walletInfo) {
      return null;
    }
    setSocialAuthType('tonconnect');

    const path = isStepByStepEnabled
      ? '/authenticate'
      : '/complete-registration';

    const optionalVariables = asPath.includes(path)
      ? {
          username: values?.username,
          password: values?.password,
          email: values?.email,
          phone: values?.phone,
          offers: values?.offers,
          dateOfBirth: values?.dateOfBirth,
          language,
          refAff,
          friendRef,
          refCode,
          cid,
        }
      : {};

    const account: Account = walletInfo.account;

    delete account.publicKey;

    const tonProof: TonProofItemReplySuccess = (walletInfo.connectItems
      ?.tonProof as TonProofItemReplySuccess) || {
      name: 'ton_proof',
      proof: {
        timestamp: 0,
        domain: {
          lengthBytes: 0,
          value: '',
        },
        payload: '',
        signature: '',
      },
    };

    const flatTonProof = {
      proofName: tonProof.name,
      timestamp: tonProof.proof.timestamp,
      domainLengthBytes: tonProof.proof.domain.lengthBytes,
      domain: tonProof.proof.domain.value,
      signature: tonProof.proof.signature,
      proofPayload: tonProof.proof.payload,
      site,
    };

    const variables: TonConnectAuthMutationVariables = {
      input: {
        ...optionalVariables,
        ...account,
        ...flatTonProof,
        otp,
      },
    };

    const { data } = await tonConnectAuth({ variables });
    const { deviceDataToken, errors, token } =
      data?.userManagementTonConnectAuthenticate ?? {};

    handleAuthErrors({
      errors,
      setError,
      setErrors,
      setOtpError,
      redirectUrl,
      address: account.address,
    });

    if (token) {
      Cookie.set('token', token);
      startFraudEngine({ deviceDataToken });
      await updateGtmUserData({ authToken: token });
      trackEvent({}, 'registrationSuccess');
      trackEvent({}, 'completeRegistrationSuccess');
      requestEmailVerification();
      dismissPopover(true);
      return nativeReplace(redirectUrl);
    }

    return null;
  };
  return { loading, handleTonConnectAuth };
};

export default useAuthTonConnectUser;
